import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, apiErr } from "../../api/client";
import { ADMIN } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, FileText, Mail } from "lucide-react";

function mondayOf(d) {
    const dt = new Date(d);
    const dow = (dt.getDay() + 6) % 7;
    dt.setDate(dt.getDate() - dow);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
function toIsoDate(d) { return d.toISOString().slice(0, 10); }
function fmtRange(monday) {
    const sun = new Date(monday); sun.setDate(sun.getDate() + 6);
    const opts = { day: "numeric", month: "short" };
    return `${monday.toLocaleDateString("en-AU", opts)} – ${sun.toLocaleDateString("en-AU", { ...opts, year: "numeric" })}`;
}
function money(n) {
    if (n === null || n === undefined) return "—";
    return `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

const TS_STATUS_MAP = {
    not_started: "legacy",
    draft: "on_hold",
    submitted: "on_hold",
    approved: "active",
    rejected: "legacy",
};

export default function Reports() {
    const [weekMon, setWeekMon] = useState(toIsoDate(mondayOf(new Date())));
    const [data, setData] = useState(null);
    const [busyEmail, setBusyEmail] = useState(false);
    const monday = useMemo(() => new Date(weekMon + "T00:00"), [weekMon]);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/v1/admin/reports/payroll", { params: { week: weekMon } });
            setData(data);
        } catch (e) { toast.error(apiErr(e)); }
    }, [weekMon]);
    useEffect(() => { load(); }, [load]);

    const emailBookkeeper = async () => {
        setBusyEmail(true);
        try {
            const { data } = await api.post(`/v1/reports/payroll/${weekMon}/email`, {});
            toast.success(`Stubbed · would send ${data.attachments.length} PDFs to ${data.to} · logged to audit`);
        } catch (e) { toast.error(apiErr(e)); } finally { setBusyEmail(false); }
    };

    const shift = (n) => {
        const d = new Date(monday); d.setDate(d.getDate() + n * 7);
        setWeekMon(toIsoDate(d));
    };

    const downloadCsv = async (path) => {
        try {
            const url = `${BACKEND}${path}`;
            const resp = await fetch(url, { credentials: "include" });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const disp = resp.headers.get("Content-Disposition") || "";
            const match = disp.match(/filename="?([^"]+)"?/);
            const fname = match ? match[1] : "export.csv";
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = fname;
            document.body.appendChild(link); link.click(); link.remove();
            toast.success(`Downloaded ${fname}`);
        } catch (e) { toast.error(`Download failed: ${e.message}`); }
    };

    return (
        <div data-testid={ADMIN.reportsRoot} className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="eyebrow">Admin</div>
                    <h1 className="text-4xl font-extrabold tracking-[-0.03em] leading-none">Reports</h1>
                    <p className="text-muted mt-2 text-sm">Weekly payroll preview + one-tap CSV exports for the bookkeeper.</p>
                </div>
                <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => shift(-1)}><ChevronLeft size={16} /></Button>
                    <div className="num text-sm px-3 min-w-[200px] text-center">{fmtRange(monday)}</div>
                    <Button variant="outline" size="sm" onClick={() => shift(1)}><ChevronRight size={16} /></Button>
                </div>
            </div>

            {/* Payroll preview table */}
            {!data ? (
                <div className="text-muted text-sm p-8">Loading…</div>
            ) : (
                <>
                    <div className="km-card overflow-hidden" data-testid={ADMIN.reportsTable}>
                        <div className="px-5 py-4 border-b border-km flex items-center justify-between flex-wrap gap-2">
                            <h2 className="text-lg font-extrabold tracking-[-0.03em]">Payroll preview</h2>
                            <div className="text-xs text-muted num">
                                Total std {data.totals.std_hrs.toFixed(1)}h · OT {data.totals.ot_hrs.toFixed(1)}h · KMs {data.totals.km.toFixed(0)} · <b className="text-ink">{money(data.totals.dollars)}</b>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted border-b border-km">
                                        <th className="px-4 py-3 font-medium">Staff</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium text-right">Std hrs</th>
                                        <th className="px-4 py-3 font-medium text-right">OT hrs</th>
                                        <th className="px-4 py-3 font-medium text-right">KMs</th>
                                        <th className="px-4 py-3 font-medium text-right">Payable</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.rows.map((r) => (
                                        <tr key={r.user.id} className="border-b border-km hover:bg-[color:var(--surface-2)]">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <InitialsAvatar name={r.user.name} initials={r.user.initials} size={28} />
                                                    <div>
                                                        <div className="text-ink text-sm">{r.user.name}</div>
                                                        <div className="text-[10px] uppercase tracking-wider text-muted">{r.user.role}{r.user.salaried ? " · salaried" : ""}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={TS_STATUS_MAP[r.timesheet_status] || "legacy"} />
                                                <span className="text-[10px] text-muted ml-2 uppercase tracking-wider">{r.timesheet_status.replace("_", " ")}</span>
                                            </td>
                                            <td className="px-4 py-3 num text-right">{r.std_hrs.toFixed(1)}</td>
                                            <td className="px-4 py-3 num text-right">
                                                <span className={r.ot_hrs > 0 ? "text-[color:var(--km-red)]" : ""}>{r.ot_hrs.toFixed(1)}</span>
                                            </td>
                                            <td className="px-4 py-3 num text-right">{r.km.toFixed(0)}</td>
                                            <td className="px-4 py-3 num text-right font-medium">
                                                {r.user.salaried ? <span className="text-muted text-xs italic">salaried</span> : money(r.dollars)}
                                            </td>
                                        </tr>
                                    ))}
                                    {data.rows.length === 0 && (
                                        <tr><td colSpan={6} className="py-8 text-center text-muted text-sm">No active staff.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Export actions */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <ExportCard
                            title="Weekly payroll CSV"
                            hint="This-week rows for Xero/Excel"
                            onClick={() => downloadCsv(`/api/v1/export/time-entries.csv?since=${weekMon}T00:00:00%2B10:00&until=${nextMonday(weekMon)}T00:00:00%2B10:00`)}
                            testid={ADMIN.reportsExportTime}
                        />
                        <ExportCard
                            title="All jobs"
                            hint="Every job + status + quote"
                            onClick={() => downloadCsv("/api/v1/export/jobs.csv")}
                            testid={ADMIN.reportsExportJobs}
                        />
                        <ExportCard
                            title="Staff rates"
                            hint="Current active rates"
                            onClick={() => downloadCsv("/api/v1/export/users.csv")}
                            testid={ADMIN.reportsExportUsers}
                        />
                    </div>

                    {/* Phase 5 · PDFs live */}
                    <div className="km-card p-5">
                        <div className="text-[11px] uppercase tracking-[0.14em] text-muted mb-3">PDF preview &amp; email</div>
                        <div className="flex gap-3 flex-wrap">
                            <Button
                                data-testid={ADMIN.reportsPdf}
                                onClick={() => window.open(`${BACKEND}/api/v1/reports/payroll/${weekMon}.zip`, "_blank")}
                                variant="outline"
                                className="gap-2"
                                title="Downloads a zip of every employee's payroll PDF for this week"
                            >
                                <FileText size={14} /> Preview PDFs (ZIP)
                            </Button>
                            <Button
                                data-testid={ADMIN.reportsEmail}
                                onClick={emailBookkeeper}
                                variant="outline"
                                className="gap-2"
                                disabled={busyEmail}
                            >
                                <Mail size={14} /> Email to bookkeeper
                            </Button>
                        </div>
                        <div className="text-[11px] text-muted mt-3">
                            Live PDF: KMSurveys letterhead · matches this table exactly · salaried users hide $ · rate overrides shown.
                            Email step is <b>stubbed</b> until Michael wires SMTP at production deploy — the request is logged to the audit trail.
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function nextMonday(week) {
    const d = new Date(week + "T00:00");
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
}

function ExportCard({ title, hint, onClick, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className="km-card km-card-interactive p-5 text-left w-full"
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="font-medium text-ink text-sm">{title}</div>
                    <div className="text-xs text-muted mt-1">{hint}</div>
                </div>
                <Download size={16} className="text-muted" />
            </div>
        </button>
    );
}
